import { ENABLE_DEBUG_LOGS, WS_URL } from '../utils/config';
class WebSocketService {
    constructor() {
        this.ws = null;
        this.roomCallbacks = new Map();
        this.roomMessageCallbacks = new Map();
        this.reconnectAttempts = 0;
        this.maxReconnectAttempts = 5;
        this.isUserActive = true;
        this.isTabVisible = true;
        this.activityTimer = null;
        this.connectionStatusCallbacks = new Set();
        this.isConnecting = false; // Add this flag
        this.setupActivityDetection();
        this.setupVisibilityDetection();
    }
    setupActivityDetection() {
        const resetTimer = () => {
            this.isUserActive = true;
            this.checkConnectionState();
            if (this.activityTimer)
                clearTimeout(this.activityTimer);
            this.activityTimer = setTimeout(() => {
                this.isUserActive = false;
                this.checkConnectionState();
            }, 5 * 60 * 1000); // 5 minutes
        };
        ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'].forEach((event) => {
            document.addEventListener(event, resetTimer, true);
        });
        resetTimer(); // Start timer
    }
    setupVisibilityDetection() {
        document.addEventListener('visibilitychange', () => {
            this.isTabVisible = !document.hidden;
            this.checkConnectionState();
            if (ENABLE_DEBUG_LOGS)
                console.log('visibilitychange', this.isTabVisible);
        });
    }
    checkConnectionState() {
        const shouldBeConnected = this.isTabVisible && this.isUserActive;
        if (shouldBeConnected && !this.ws) {
            this.connect();
        }
        else if (!shouldBeConnected && this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.disconnect();
        }
    }
    notifyConnectionStatusChange() {
        this.connectionStatusCallbacks.forEach((callback) => {
            try {
                callback();
            }
            catch (error) {
                console.error('Error in connection status callback:', error);
            }
        });
    }
    saveToCache(room, message) {
        const serverTimestamp = message.timestamp * 1000;
        const cacheData = {
            message: message,
            serverTimestamp: serverTimestamp,
            cachedAt: Date.now(),
            lastUpdated: Date.now(),
        };
        localStorage.setItem(`ws-room-${room}`, JSON.stringify(cacheData));
    }
    getCachedData(room) {
        const cached = localStorage.getItem(`ws-room-${room}`);
        if (!cached)
            return null;
        try {
            const { message, serverTimestamp } = JSON.parse(cached);
            // Ensure we have valid data and timestamp
            if (!message || typeof serverTimestamp !== 'number') {
                localStorage.removeItem(`ws-room-${room}`);
                return null;
            }
            // Calculate data age based on server timestamp
            const dataAge = Date.now() - serverTimestamp;
            const isStale = dataAge > 60000; // Data older than 1 minute from server
            return {
                message,
                serverTimestamp,
                isStale,
            };
        }
        catch (error) {
            console.error('Error parsing cached data:', error);
            localStorage.removeItem(`ws-room-${room}`);
            return null;
        }
    }
    connect() {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            return;
        }
        if (this.isConnecting) {
            return; // Prevent multiple connection attempts
        }
        this.isConnecting = true;
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
        console.log(`Attempting WebSocket connection to: ${WS_URL}`);
        try {
            this.ws = new WebSocket(WS_URL);
            this.notifyConnectionStatusChange(); // Notify that we're now connecting
        }
        catch (error) {
            console.error('Failed to create WebSocket:', error);
            this.isConnecting = false; // Reset flag on error
            this.notifyConnectionStatusChange(); // Notify about the error
            return;
        }
        this.ws.onopen = () => {
            console.log('WebSocket connected successfully');
            this.isConnecting = false; // Reset flag on successful connection
            this.reconnectAttempts = 0;
            this.notifyConnectionStatusChange(); // Notify listeners on successful connection
            // Re-subscribe to all active rooms
            this.roomCallbacks.forEach((callbacks, room) => {
                if (callbacks.size > 0) {
                    this.sendSubscription(room);
                }
            });
            this.roomMessageCallbacks.forEach((callbacks, room) => {
                if (callbacks.size > 0) {
                    this.sendSubscription(room);
                }
            });
        };
        this.ws.onmessage = (event) => {
            var _a, _b;
            try {
                const message = JSON.parse(event.data);
                // Fires on every server broadcast — gated so it doesn't flood the console.
                if (ENABLE_DEBUG_LOGS)
                    console.log('message', message);
                if (message.room && message.data) {
                    this.saveToCache(message.room, message);
                    if (this.roomCallbacks.has(message.room)) {
                        (_a = this.roomCallbacks.get(message.room)) === null || _a === void 0 ? void 0 : _a.forEach((callback) => {
                            try {
                                callback(message.data);
                            }
                            catch (error) {
                                console.error(`Error in subscriber callback for room ${message.room}:`, error);
                            }
                        });
                    }
                    if (this.roomMessageCallbacks.has(message.room)) {
                        (_b = this.roomMessageCallbacks.get(message.room)) === null || _b === void 0 ? void 0 : _b.forEach((callback) => {
                            try {
                                callback(message);
                            }
                            catch (error) {
                                console.error(`Error in subscriber callback for room ${message.room}:`, error);
                            }
                        });
                    }
                }
            }
            catch (error) {
                console.error('Error parsing WebSocket message:', error);
            }
        };
        this.ws.onclose = (event) => {
            console.log(`WebSocket closed. Code: ${event.code}, Reason: ${event.reason}`);
            this.ws = null;
            this.isConnecting = false; // Reset flag on close
            this.notifyConnectionStatusChange(); // Notify listeners on disconnection
            // Reconnect if should be connected
            if (this.isTabVisible &&
                this.isUserActive &&
                this.reconnectAttempts < this.maxReconnectAttempts) {
                this.reconnectAttempts++;
                const delay = 1000 * this.reconnectAttempts;
                console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);
                setTimeout(() => this.connect(), delay);
            }
        };
        this.ws.onerror = (error) => {
            var _a;
            console.warn('WebSocket error:', error);
            console.warn('WebSocket state:', (_a = this.ws) === null || _a === void 0 ? void 0 : _a.readyState);
            console.warn('WebSocket URL:', WS_URL);
            this.isConnecting = false; // Reset flag on error
            this.notifyConnectionStatusChange(); // Notify listeners on error
        };
    }
    disconnect() {
        if (this.ws) {
            this.ws.close();
            this.ws = null;
        }
    }
    subscribe(room, callback) {
        if (!this.roomCallbacks.has(room)) {
            this.roomCallbacks.set(room, new Set());
            // Send subscription to server if connected
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendSubscription(room);
            }
        }
        this.roomCallbacks.get(room).add(callback);
        // Ensure connection if user is active and tab is visible (with small delay for server startup)
        if (!this.ws && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.ws && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100); // 100ms delay to allow server to fully start
        }
        // Return cached data immediately to avoid blank pages
        const cachedData = this.getCachedData(room);
        if (cachedData) {
            try {
                callback(cachedData.message.data);
            }
            catch (error) {
                console.error(`Error calling callback with cached data for room ${room}:`, error);
            }
        }
    }
    subscribeMessage(room, callback) {
        if (!this.roomCallbacks.has(room) && !this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.set(room, new Set());
            // Send subscription to server if connected
            if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                this.sendSubscription(room);
            }
        }
        else if (!this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.set(room, new Set());
        }
        this.roomMessageCallbacks.get(room).add(callback);
        // Ensure connection if user is active and tab is visible (with small delay for server startup)
        if (!this.ws && this.isTabVisible && this.isUserActive) {
            setTimeout(() => {
                if (!this.ws && this.isTabVisible && this.isUserActive) {
                    this.connect();
                }
            }, 100); // 100ms delay to allow server to fully start
        }
        // Return cached data immediately to avoid blank pages
        const cachedData = this.getCachedData(room);
        if (cachedData) {
            try {
                callback(cachedData.message);
            }
            catch (error) {
                console.error(`Error calling callback with cached data for room ${room}:`, error);
            }
        }
    }
    unsubscribe(room, callback) {
        if (this.roomCallbacks.has(room)) {
            this.roomCallbacks.get(room).delete(callback);
            // If no more callbacks, unsubscribe from server
            if (this.roomCallbacks.get(room).size === 0 && !this.roomMessageCallbacks.has(room)) {
                this.roomCallbacks.delete(room);
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.sendUnsubscription(room);
                }
            }
        }
    }
    unsubscribeMessage(room, callback) {
        if (this.roomMessageCallbacks.has(room)) {
            this.roomMessageCallbacks.get(room).delete(callback);
            // If no more callbacks, unsubscribe from server
            if (this.roomMessageCallbacks.get(room).size === 0 &&
                (!this.roomCallbacks.has(room) || this.roomCallbacks.get(room).size === 0)) {
                this.roomMessageCallbacks.delete(room);
                if (this.ws && this.ws.readyState === WebSocket.OPEN) {
                    this.sendUnsubscription(room);
                }
            }
        }
    }
    sendSubscription(room) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ action: 'subscribe', room }));
        }
    }
    sendUnsubscription(room) {
        if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({ action: 'unsubscribe', room }));
        }
    }
    // Public method to get connection status (useful for UI)
    getConnectionStatus() {
        if (!this.ws)
            return 'disconnected';
        switch (this.ws.readyState) {
            case WebSocket.OPEN:
                return 'connected';
            case WebSocket.CONNECTING:
                return 'connecting';
            default:
                return 'disconnected';
        }
    }
    // Public method to get activity status (useful for diagnostic UI)
    getActivityStatus() {
        return {
            isUserActive: this.isUserActive,
            isTabVisible: this.isTabVisible,
        };
    }
    onConnectionStatusChange(callback) {
        this.connectionStatusCallbacks.add(callback);
        // Return unsubscribe function
        return () => {
            this.connectionStatusCallbacks.delete(callback);
        };
    }
    // Send transaction notification to WebSocket server
    sendTransactionNotification(poolId, address) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket not connected, cannot send transaction notification');
            return;
        }
        const notification = {
            action: 'transaction',
            tx_type: `pool:${poolId}`,
            address: address,
        };
        try {
            this.ws.send(JSON.stringify(notification));
            console.log('Transaction notification sent:', notification);
        }
        catch (error) {
            console.error('Error sending transaction notification:', error);
        }
    }
    // Send claim rewards notification to WebSocket server
    sendClaimRewardsNotification(address) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket not connected, cannot send claim rewards notification');
            return;
        }
        const notification = {
            action: 'transaction',
            tx_type: 'claim_rewards',
            address: address,
        };
        try {
            this.ws.send(JSON.stringify(notification));
            console.log('Claim rewards notification sent:', notification);
        }
        catch (error) {
            console.error('Error sending claim rewards notification:', error);
        }
    }
    // Send vote notification to WebSocket server to refresh governance data
    sendVoteNotification(address, proposalId) {
        if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
            console.warn('WebSocket not connected, cannot send vote notification');
            return;
        }
        const notification = {
            action: 'transaction',
            tx_type: 'vote',
            address,
            proposal_id: proposalId,
        };
        try {
            this.ws.send(JSON.stringify(notification));
            console.log('Vote notification sent:', notification);
        }
        catch (error) {
            console.error('Error sending vote notification:', error);
        }
    }
}
// Simple export
export const webSocketService = new WebSocketService();
